<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="mb-8">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('Notifications.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('Notifications.subtitle') }}
            </p>
          </div>

          <div class="flex items-center space-x-2">
            <button
              v-if="hasUnreadNotifications"
              type="button"
              class="btn btn--secondary-blue text-sm"
              @click="markAllAsRead"
            >
              {{ $t('Notifications.markAllRead') }}
            </button>

            <button
              v-if="notifications && notifications.total > 0"
              type="button"
              class="btn btn--secondary-red text-sm"
              @click="deleteAllNotifications"
            >
              {{ $t('Notifications.deleteAll') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- Table Header with Search -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="space-y-4">
            <!-- Filter Header -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-5 h-5 text-gray-600">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
    />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('Notifications.filters') }}
                </h3>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="notifications"
                  key="total-entries"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900">{{ notifications.total }}&nbsp;</span>

                  <span class="text-gray-500">{{ $t('Notifications.totalEntries') }}</span>
                </p>
              </Transition>
            </div>

            <!-- Filter Controls -->
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <!-- Project Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('Notifications.project') }}
                </label>

                <AppSelect
                  v-model="filterForm.form.project_id"
                  name="project_filter"
                  class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  :options="projectOptions"
                  :empty-text="$t('Notifications.allProjects')"
                />
              </div>

              <!-- Priority Level Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('Notifications.priorityLevel') }}
                </label>

                <AppSelect
                  v-model="filterForm.form.priority_level"
                  name="priority_level_filter"
                  class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  :options="priorityLevelOptions"
                  :empty-text="$t('Notifications.allPriorityLevels')"
                />
              </div>

              <!-- Notification Type Filter -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  {{ $t('Notifications.notificationType') }}
                </label>

                <AppSelect
                  v-model="filterForm.form.type"
                  name="notification_type_filter"
                  class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  :options="notificationTypeOptions"
                  :empty-text="$t('Notifications.allNotificationTypes')"
                />
              </div>

              <!-- Clear Filters Button -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700 opacity-0">
                  {{ $t('Notifications.actions') }}
                </label>

                <button
                  type="button"
                  class="btn btn--secondary-blue w-full text-sm"
                  @click="resetFilters"
                >
                  {{ $t('Notifications.clearFilters') }}
                </button>
              </div>
            </div>

            <!-- Active Filters Display -->
            <div
              v-if="hasActiveFilters"
              class="flex items-center space-x-2 pt-2"
            >
              <span class="text-sm text-gray-500">{{ $t('Notifications.activeFilters') }}</span>

              <div class="flex flex-wrap gap-2">
                <span
                  v-if="filterForm.form.project_id"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ getProjectName(filterForm.form.project_id) }}
                  <button
                    type="button"
                    class="ml-1.5 text-blue-600 hover:text-blue-800"
                    @click="filterForm.form.project_id = undefined"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="filterForm.form.priority_level"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getPriorityLevelColour(filterForm.form.priority_level).label"
                >
                  {{ getPriorityLevelLabel(filterForm.form.priority_level) }}
                  <button
                    type="button"
                    class="ml-1.5"
                    :class="getPriorityLevelColour(filterForm.form.priority_level).filter"
                    @click="filterForm.form.priority_level = undefined"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>

                <span
                  v-if="filterForm.form.type"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  {{ getNotificationTypeLabel(filterForm.form.type) }}
                  <button
                    type="button"
                    class="ml-1.5 text-indigo-600 hover:text-indigo-800"
                    @click="filterForm.form.type = undefined"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Top -->
        <div class="px-6 py-4 border-b border-gray-200 bg-white">
          <Pagination
            :notifications="notifications"
            :loading="indexNotificationsAction.is(States.LOADING)"
            @go-to-page="goToPage($event.page)"
          />
        </div>

        <!-- Mobile List View -->
        <div class="md:hidden">
          <!-- Loading State -->
          <div
            v-if="!indexNotificationsAction.firstLoad.value && indexNotificationsAction.is(States.LOADING)"
            class="px-6 py-16 text-center"
          >
            <div class="flex flex-col items-center">
              <AppSpinner class="mb-4" />

              <h3 class="text-sm font-medium text-gray-900 mb-1">
                {{ $t('Notifications.loading') }}
              </h3>
            </div>
          </div>

          <!-- Notifications List -->
          <div
            v-else-if="(indexNotificationsAction.is(States.COMPLETE) || indexNotificationsAction.firstLoad.value) && notifications"
            class="divide-y-2 child:border-t-white"
            :class="{ 'opacity-50 pointer-events-none': indexNotificationsAction.is(States.LOADING) }"
          >
            <div
              v-for="notification in notifications.data"
              :key="notification.id"
              class="p-4 hover:bg-gray-50 transition-colors duration-150 relative"
              :class="[!notification.read_at ? 'bg-blue-50 border-l-4 border-l-blue-500' : '']"
            >
              <div class="flex items-start space-x-3">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <p class="text-sm font-medium text-gray-900 truncate">
                      {{ notification.data.title ?? 'Notification' }}
                    </p>

                    <p class="text-xs text-gray-500 ml-2 flex-shrink-0">
                      {{ formatTimeAgo(notification.created_at) }}
                    </p>
                  </div>

                  <p class="text-sm font-medium text-gray-600 mt-1">
                    {{ notification.data.project_name }}
                  </p>

                  <p class="text-sm text-gray-600 mt-1 line-clamp-2">
                    {{ notification.data.body }}
                  </p>

                  <div
                    v-if="notification.data?.priority_level"
                    class="mt-2"
                  >
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wide"
                      :class="getPriorityLevelColour(notification.data.priority_level).label"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getPriorityLevelColour(notification.data.priority_level).dot"
                      />
                      {{ notification.data.priority_level }}
                    </span>
                  </div>

                  <div
                    v-if="notification.data.popup_component"
                    class="mt-2"
                  >
                    <button
                      type="button"
                      class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      @click="openNotificationPopup(notification)"
                    >
                      {{ notification.data.popup_text ?? 'View' }}
                    </button>
                  </div>

                  <div
                    v-else-if="notification.data.action_url"
                    class="mt-2"
                  >
                    <button
                      type="button"
                      class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      @click="handleNotificationAction(notification.data.action_url)"
                    >
                      {{ notification.data.action }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Delete button on the right -->
              <button
                type="button"
                class="absolute top-4 right-4 p-1 rounded-md hover:bg-red-100 transition-colors duration-200 flex-shrink-0"
                :aria-label="$t('Notifications.deleteNotification')"
                @click="deleteNotification(notification.id)"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4 text-red-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
              </button>
            </div>

            <!-- Empty State -->
            <div
              v-if="notifications.total === 0"
              class="px-6 py-16 text-center"
            >
              <div class="flex flex-col items-center">
                <svg
fill="none"
viewBox="0 0 24 24"
stroke-width="1.5" stroke="currentColor" class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
</svg>

                <h3 class="text-sm font-medium text-gray-900 mb-1">
                  {{ $t('Notifications.noNotificationsFound') }}
                </h3>

                <p
                  v-if="hasActiveFilters"
                  class="text-sm text-gray-500"
                >
                  {{ $t('Notifications.adjustFilters') }}
                </p>

                <p
                  v-else
                  class="text-sm text-gray-500"
                >
                  {{ $t('Notifications.getStarted') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('Notifications.notification') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('Notifications.project') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('Notifications.date') }}
                </th>

                <th class="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" />
              </tr>
            </thead>

            <tbody class="bg-white divide-y child:border-t-gray-200">
              <!-- Loading State -->
              <tr v-if="!indexNotificationsAction.firstLoad.value && indexNotificationsAction.is(States.LOADING)">
                <td
                  colspan="4"
                  class="px-6 py-16 text-center"
                >
                  <div class="flex flex-col items-center">
                    <AppSpinner class="mb-4" />

                    <h3 class="text-sm font-medium text-gray-900 mb-1">
                      {{ $t('Notifications.loading') }}
                    </h3>
                  </div>
                </td>
              </tr>

              <!-- Data Rows -->
              <template v-else-if="(indexNotificationsAction.is(States.COMPLETE) || indexNotificationsAction.firstLoad.value) && notifications">
                <tr
                  v-for="notification in notifications.data"
                  :key="notification.id"
                  class="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                  :class="{ 'opacity-50 pointer-events-none': indexNotificationsAction.is(States.LOADING), 'bg-blue-50 border-l-4 border-l-blue-500': !notification.read_at }"
                >
                  <td class="px-6 py-5">
                    <div class="flex items-start justify-between">
                      <div class="flex items-center space-x-2 flex-1">
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-900 truncate">
                            {{ notification.data.title ?? 'Notification' }}
                          </p>

                          <p class="text-sm text-gray-600 mt-1 line-clamp-2">
                            {{ notification.data.body }}
                          </p>

                          <div
                            v-if="notification.data.popup_component"
                            class="mt-2"
                          >
                            <button
                              type="button"
                              class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              @click="openNotificationPopup(notification)"
                            >
                              {{ notification.data.popup_text ?? 'View' }}
                            </button>
                          </div>

                          <div
                            v-else-if="notification.data.action_url"
                            class="mt-2"
                          >
                            <button
                              type="button"
                              class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                              @click="handleNotificationAction(notification.data.action_url)"
                            >
                              {{ notification.data.action }}
                            </button>
                          </div>
                        </div>

                        <div
                          v-if="notification.data?.priority_level"
                          class="ml-4 flex-shrink-0"
                        >
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase tracking-wide"
                            :class="getPriorityLevelColour(notification.data.priority_level).label"
                          >
                            <span
                              class="w-1.5 h-1.5 rounded-full mr-1.5"
                              :class="getPriorityLevelColour(notification.data.priority_level).dot"
                            />
                            {{ notification.data.priority_level }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="px-6 py-5 whitespace-nowrap">
                    <p class="text-sm font-medium text-gray-900">
                      {{ notification.data?.project_name }}
                    </p>
                  </td>

                  <td class="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                    <div class="space-y-1">
                      <div class="font-medium text-gray-900">
                        {{ formatTimeAgo(notification.created_at) }}
                      </div>

                      <div class="text-gray-500 text-xs">
                        {{ formatDateTime(notification.created_at) }}
                      </div>
                    </div>
                  </td>

                  <td class="px-6 py-5 whitespace-nowrap text-right">
                    <button
                      type="button"
                      class="p-1 rounded-md hover:bg-red-100 transition-colors duration-200"
                      :aria-label="$t('Notifications.deleteNotification')"
                      @click="deleteNotification(notification.id)"
                    >
                      <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4 text-red-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                    </button>
                  </td>
                </tr>

                <!-- Empty State -->
                <tr v-if="notifications.total === 0">
                  <td
                    colspan="4"
                    class="px-6 py-16 text-center"
                  >
                    <div class="flex flex-col items-center">
                      <svg
fill="none"
viewBox="0 0 24 24"
stroke-width="1.5" stroke="currentColor" class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
</svg>

                      <h3 class="text-sm font-medium text-gray-900 mb-1">
                        {{ $t('Notifications.noNotificationsFound') }}
                      </h3>

                      <p
                        v-if="hasActiveFilters"
                        class="text-sm text-gray-500"
                      >
                        {{ $t('Notifications.adjustFilters') }}
                      </p>

                      <p
                        v-else
                        class="text-sm text-gray-500"
                      >
                        {{ $t('Notifications.getStarted') }}
                      </p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Pagination Bottom -->
        <div class="px-6 py-4 border-t border-gray-200 bg-white">
          <Pagination
            :notifications="notifications"
            :loading="indexNotificationsAction.is(States.LOADING)"
            @go-to-page="goToPage($event.page)"
          />
        </div>
      </div>
    </div>

    <Teleport to="#modals">
      <component
        :is="notificationComponentMap[popupNotification.data.popup_component]"
        v-if="popupNotification"
        :notification="popupNotification"
        @deselect="popupNotification = undefined"
        @reload="onNotificationPopupSuccess"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import ContractEventNotificationPopup from '@/components/layout/notifications/ContractEventNotificationPopup.vue';
import Pagination from '@/components/Notifications/Pagination.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { getLinkFromActionUrl } from '@/helpers';
import { t } from '@/i18n';
import NotificationsService from '@/services/NotificationsService';
import { useNotificationsStore } from '@/store/notifications';
import { useProjectsStore } from '@/store/projects';
import {
  NOTIFICATION_SETTINGS_KEYS,
  NOTIFICATION_SETTINGS_LABELS,
  type NotificationSettingKey,
} from '@/types/notification-settings';
import { useDebounceFn } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId?: string;
}>();

const pageQuery = useRouteQuery('page', '1', { transform: Number });
const notifications = ref<Paginator<AppNotification>>(undefined);
const popupNotification = ref<AppNotification | undefined>(undefined);
const projectsStore = useProjectsStore();
const router = useRouter();
const notificationsStore = useNotificationsStore();
const indexNotificationsAction = useStoreApiAction(notificationsStore.fetchNotifications);
const destroyNotificationAction = useStoreApiAction(notificationsStore.destroy);
const destroyAllNotificationsAction = useStoreApiAction(notificationsStore.destroyAll);

const notificationComponentMap = {
  'contract-event-notification-popup': ContractEventNotificationPopup,
};

// Filter form
const filterForm = useFormData({
  project_id: undefined,
  priority_level: undefined,
  type: undefined as NotificationSettingKey | undefined,
});

// Filter options
const projectOptions = computed(() => {
  return projectsStore.models.whereNull('archived_at').get().map((project) => ({
    value: project.id.toString(),
    label: project.name,
  }));
});

const priorityLevelOptions = computed(() => [
  { value: 'low', label: t('Notifications.priorityLevels.low') },
  { value: 'medium', label: t('Notifications.priorityLevels.medium') },
  { value: 'high', label: t('Notifications.priorityLevels.high') },
  { value: 'critical', label: t('Notifications.priorityLevels.critical') },
]);

const notificationTypeOptions = computed(() =>
  NOTIFICATION_SETTINGS_KEYS.map((key) => ({
    value: key,
    label: NOTIFICATION_SETTINGS_LABELS[key],
  }))
);

// Computed properties for filter display
const hasActiveFilters = computed(() => {
  return filterForm.form.project_id || filterForm.form.priority_level || filterForm.form.type;
});

const hasUnreadNotifications = computed(() => {
  return notifications.value?.data?.some((notification) => !notification.read_at) || false;
});

const getProjectName = (projectId: string | number): string => {
  if(!projectId) return '';

  const project = projectsStore.models.find(typeof projectId === 'string' ? parseInt(projectId) : projectId);

  return project?.name || '';
};

const getPriorityLevelLabel = (level: string): string => {
  const option = priorityLevelOptions.value.find((opt) => opt.value === level);

  return option ? option.label : level;
};

const getNotificationTypeLabel = (key: string): string => {
  return NOTIFICATION_SETTINGS_LABELS[key as NotificationSettingKey] ?? key;
};

// Priority level color mapping
const getPriorityLevelColour = (level: string): { label: string; dot: string; filter: string } => {
  const colorMap: Record<string, { label: string; dot: string; filter: string }> = {
    low: {
      label: 'bg-blue-50 text-blue-700 border border-blue-200',
      dot: 'bg-blue-500',
      filter: 'text-blue-600 hover:text-blue-800',
    },

    medium: {
      label: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      dot: 'bg-yellow-500',
      filter: 'text-yellow-600 hover:text-yellow-800',
    },

    high: {
      label: 'bg-orange-50 text-orange-700 border border-orange-200',
      dot: 'bg-orange-500',
      filter: 'text-orange-600 hover:text-orange-800',
    },

    critical: {
      label: 'bg-red-50 text-red-700 border border-red-200',
      dot: 'bg-red-500',
      filter: 'text-red-600 hover:text-red-800',
    },
  };

  return colorMap[level] ?? {
    label: 'bg-gray-50 text-gray-700 border border-gray-200',
    dot: 'bg-gray-500',
    filter: 'text-gray-600 hover:text-gray-800',
  };
};

const resetFilters = () => {
  filterForm.resetData();
};

const applyFilters = () => {
  router.replace({
    query: {
      page: 1,
      ...filterForm.form,
    },
  });

  indexNotificationsAction.request({ page: 1, ...filterForm.form }).then((data) => {
    if(data) {
      notifications.value = data;
    }
  });
};

const debouncedApplyFilters = useDebounceFn(applyFilters, 300);

// Watch for filter changes
watch(filterForm.form, () => {
  debouncedApplyFilters();
}, { deep: true });

// Load initial data
onMounted(() => {
  // Set initial project filter if projectId prop is provided
  if(props.projectId) {
    filterForm.form.project_id = props.projectId;
  }

  applyFilters();
});

// Pagination functions
const goToPage = (page: number) => {
  pageQuery.value = page;

  indexNotificationsAction.request({ page: pageQuery.value, ...filterForm.form }).then((data) => {
    if(data) {
      notifications.value = data;
    }
  });
};

const markAllAsRead = async () => {
  await NotificationsService.markAllAsRead();
  // Refresh the notifications list
  applyFilters();
};

const deleteNotification = async (notificationId: string) => {
  const result = await Swal.fire({
    icon: 'question',
    title: t('Notifications.deleteTitle'),
    text: t('Notifications.deleteConfirmation'),
    showCancelButton: true,
    confirmButtonText: t('Notifications.delete'),
    cancelButtonText: t('Notifications.cancel'),
  });

  if(result.isConfirmed) {
    destroyNotificationAction.request(notificationId).then(() => {
      // Update local state immediately
      if(notifications.value) {
        notifications.value.data = notifications.value.data.filter((n) => n.id !== notificationId);
        notifications.value.total = Math.max(0, notifications.value.total - 1);
      }

      // If we're on a page that becomes empty, go to previous page
      if(notifications.value && notifications.value.data.length === 0 && pageQuery.value > 1) {
        goToPage(pageQuery.value - 1);
      } else {
        // Refresh to ensure consistency
        applyFilters();
      }
    });
  }
};

const deleteAllNotifications = async () => {
  const result = await Swal.fire({
    icon: 'warning',
    title: t('Notifications.deleteAllTitle'),
    text: t('Notifications.deleteAllConfirmation'),
    showCancelButton: true,
    confirmButtonText: t('Notifications.deleteAll'),
    cancelButtonText: t('Notifications.cancel'),
    confirmButtonColor: '#dc2626',
  });

  if(result.isConfirmed) {
    destroyAllNotificationsAction.request().then(() => {
      // Update local state immediately
      if(notifications.value) {
        notifications.value.data = [];
        notifications.value.total = 0;
      }

      // Refresh to ensure consistency
      applyFilters();
    }).catch((error) => {
      console.error('Error deleting all notifications:', error);

      Swal.fire({
        icon: 'error',
        title: t('Notifications.deleteAllError'),
        text: t('Notifications.deleteAllErrorMessage'),
      });
    });
  }
};

const openNotificationPopup = (notification: AppNotification) => {
  popupNotification.value = notification;
};

const onNotificationPopupSuccess = () => {
  popupNotification.value = undefined;
  goToPage(pageQuery.value);
};

const handleNotificationAction = (url: string) => {
  // Handle navigation based on notification type or data
  router.push(getLinkFromActionUrl(url));
};

// Format time ago using Luxon (similar to diffForHumans)
const formatTimeAgo = (dateString: string): string => {
  return DateTime.fromISO(dateString).toRelative();
};

// Format date time using Luxon
const formatDateTime = (dateString: string): string => {
  const date = DateTime.fromISO(dateString);

  return date.toFormat('dd/MM/yyyy h:mm a');
};
</script>

<style scoped></style>
